import {useAgentSessionsAlive} from '@github-ui/agent-sessions/hooks/use-agent-sessions-alive'
import {getReadableSessionState as getAgentSessionState} from '@github-ui/agent-sessions/types/session'
import type {APITask, APITaskGroup} from '@github-ui/agent-sessions/types/api-task'
import {
  groupedTasksApiPathWithParams,
  TASK_TYPE_CHAT,
  taskByIdApiPath,
  tasksApiPathWithParams,
} from '@github-ui/agent-sessions/utils/api-url-helpers'
import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {resolveTaskOwnerLogin} from '@github-ui/copilot-shared-ui/utils/task-owner'
import {CopilotChatService} from '@github-ui/copilot-chat/utils/copilot-chat-service'
import type {CopilotChatOrg, CopilotChatThread} from '@github-ui/copilot-chat-types'
import {useInfiniteQuery, useQuery, useQueryClient, type InfiniteData} from '@github-ui/react-query'
import {useDebounce} from '@github-ui/use-debounce'
import {useCallback, useEffect, useMemo, useRef} from 'react'

import type {AhpSessionIdentifiers, ChatDisplayIdentity, ChatTask} from '../types'
import {type ChatProject, parseProjectNwo} from './project-session'
import {useHiddenEarlyTaskIds} from '@github-ui/agent-sessions/ahp/early-task-visibility'

export const CHAT_SIDEBAR_QUERY_KEY_PREFIX = 'chat-history-sidebar' as const
export const CHAT_HISTORY_QUERY_KEY_PREFIX = 'chat-history-list' as const
export const CHAT_SESSION_QUERY_KEY_PREFIX = 'chat-history-restore' as const
export const CHAT_EVENTS_HISTORY_QUERY_KEY_PREFIX = 'chat-history-events' as const
export const CHAT_RESUME_SESSION_QUERY_KEY_PREFIX = 'chat-history-resume-session' as const
export const LEGACY_CHAT_HISTORY_QUERY_KEY_PREFIX = 'legacy-chat-history' as const

export function chatSidebarQueryKey(apiURL?: string) {
  return [CHAT_SIDEBAR_QUERY_KEY_PREFIX, apiURL] as const
}

export function chatHistoryQueryKeyPrefix(apiURL: string | undefined) {
  return [CHAT_HISTORY_QUERY_KEY_PREFIX, apiURL] as const
}

export function chatHistoryQueryKey(apiURL: string | undefined, pageSize: number) {
  return [CHAT_HISTORY_QUERY_KEY_PREFIX, apiURL, pageSize] as const
}

export function chatHistoryInfiniteQueryKey(apiURL: string | undefined, pageSize: number) {
  return [CHAT_HISTORY_QUERY_KEY_PREFIX, 'infinite', apiURL, pageSize] as const
}

export function groupedChatHistoryQueryKey(apiURL: string | undefined, maxGroups: number) {
  return [CHAT_HISTORY_QUERY_KEY_PREFIX, 'grouped', apiURL, maxGroups] as const
}

export function chatSessionQueryKey(apiURL: string, taskId: string) {
  return [CHAT_SESSION_QUERY_KEY_PREFIX, apiURL, taskId] as const
}

export function chatEventsHistoryQueryKey(apiURL: string, taskId: string) {
  return [CHAT_EVENTS_HISTORY_QUERY_KEY_PREFIX, apiURL, taskId] as const
}

export function chatResumeSessionQueryKey(apiURL: string, taskId: string) {
  return [CHAT_RESUME_SESSION_QUERY_KEY_PREFIX, apiURL, taskId] as const
}

export function useLegacyChatHistoryQuery({
  apiURL,
  ssoOrganizations,
  realIp,
  apiVersion,
}: {
  apiURL?: string
  ssoOrganizations: CopilotChatOrg[]
  realIp?: string
  apiVersion?: string
}) {
  return useQuery<{threads: CopilotChatThread[]; orgsFailedToLoad: boolean}, Error>({
    queryKey: [
      LEGACY_CHAT_HISTORY_QUERY_KEY_PREFIX,
      apiURL,
      ssoOrganizations.map(organization => organization.id),
      realIp,
      apiVersion,
    ],
    queryFn: async () => {
      const response = await new CopilotChatService(
        apiURL as string,
        ssoOrganizations,
        realIp,
        apiVersion,
      ).fetchThreads()
      if (!response.ok) throw new Error(response.error)
      return response.payload
    },
    enabled: Boolean(apiURL),
    staleTime: 30_000,
  })
}

export function getTaskTitle(task: ChatTask): string {
  return task.title || task.promptPreview || 'Untitled chat'
}

export function getTaskDatetime(task: ChatTask): string {
  return task.updatedAt || task.createdAt
}

export function getTaskTimestamp(task: ChatTask): number {
  const timestamp = Date.parse(getTaskDatetime(task))
  return Number.isNaN(timestamp) ? 0 : timestamp
}

export function getReadableChatSessionState(state: string): string {
  if (state === 'running') return 'Working'
  if (state === 'stopped') return 'Stopped'
  return getAgentSessionState(state)
}

interface ChatHistoryConfig {
  apiURL?: string
  signal?: AbortSignal
  pageSize?: number
}

interface RawChatTask extends Pick<APITask, 'current_environment' | 'compute' | 'custom_agent' | 'owner'> {
  id: string
  creator?: APITask['creator']
  sharing_status?: APITask['sharing_status']
  agent_collaborators?: APITask['agent_collaborators']
  agentSlug?: string
  nwo?: string
  name?: string
  title?: string
  problem_statement?: string
  prompt_preview?: string
  event_content?: string | null
  status?: string
  state?: string
  model?: string
  session_count?: number
  latest_session_id?: string
  latest_session_state?: string
  created_at: string
  updated_at?: string
  last_updated_at?: string
  archived_at?: string | null
}

interface RawChatSession {
  id: string
  state?: string
  environment_id?: string
  created_at?: string
  updated_at?: string
  last_updated_at?: string
  user?: {id: number; login?: string}
  agent_id?: number
  agent_slug?: string
  event_type?: string | null
  event_identifiers?: string[] | null
  event_url?: string | null
  event_content?: string | null
}

interface RawListChatTasksResponse {
  tasks?: RawChatTask[]
  has_next_page?: boolean
}

interface RawListGroupedTasksResponse {
  groups?: APITaskGroup[]
}

interface RawChatTaskDetail extends RawChatTask {
  sessions?: RawChatSession[]
  current_environment?: APITask['current_environment']
}

interface RawChatTaskDetailResponse {
  task?: RawChatTaskDetail
  sessions?: RawChatSession[]
}

const CHAT_HISTORY_PAGE_SIZE = 6
const TERMINAL_STATES = new Set(['completed', 'failed', 'cancelled', 'timed_out', 'stopped'])

function getTaskState(raw: RawChatTask): string {
  return raw.state ?? raw.status ?? 'unknown'
}

function getUpdatedAt(raw: RawChatTask): string {
  return raw.updated_at ?? raw.last_updated_at ?? raw.created_at
}

function getPromptPreview(raw: RawChatTask): string {
  return raw.prompt_preview ?? raw.problem_statement ?? raw.event_content ?? ''
}

function transformTask(raw: RawChatTask, sessions?: RawChatSession[]): ChatTask {
  const sessionCount = raw.session_count ?? sessions?.length ?? 0
  const archivedAt = raw.archived_at ?? undefined
  return {
    taskId: raw.id,
    creatorId: raw.creator?.id,
    agentSlug: raw.agent_collaborators?.[0]?.slug ?? raw.agentSlug,
    customAgentName: raw.custom_agent?.name,
    sharingStatus: raw.sharing_status,
    title: raw.title ?? raw.name ?? '',
    promptPreview: getPromptPreview(raw),
    nwo: raw.nwo,
    currentEnvironment: raw.current_environment ?? undefined,
    model: raw.model,
    createdAt: raw.created_at,
    updatedAt: getUpdatedAt(raw),
    taskState: getTaskState(raw),
    latestSessionId: raw.latest_session_id,
    latestSessionState: raw.latest_session_state ?? getTaskState(raw),
    sessionCount,
    archivedAt,
    resumable: sessionCount > 0 && !archivedAt,
    resumeBlocker: archivedAt
      ? 'This chat is archived.'
      : sessionCount === 0
        ? 'This chat does not have any sessions yet.'
        : null,
  }
}

function getSessionTimestamp(session: RawChatSession): number {
  const timestamp = session.updated_at ?? session.last_updated_at ?? session.created_at
  if (!timestamp) return 0
  const parsed = Date.parse(timestamp)
  return Number.isNaN(parsed) ? 0 : parsed
}

function pickConnectableSession(task: ChatTask, sessions: RawChatSession[]): RawChatSession | undefined {
  const latestSession = task.latestSessionId ? sessions.find(session => session.id === task.latestSessionId) : undefined
  if (latestSession?.environment_id) return latestSession

  // Task detail can omit the session association while still exposing the latest session and current environment.
  if (task.resumable && task.latestSessionId && task.currentEnvironment?.id) {
    return {
      ...latestSession,
      id: task.latestSessionId,
      environment_id: task.currentEnvironment.id,
    }
  }

  return sessions
    .filter(session => session.environment_id)
    .sort((a, b) => getSessionTimestamp(b) - getSessionTimestamp(a))[0]
}

async function fetchChatTaskDetail({
  apiURL,
  signal,
  taskId,
}: ChatHistoryConfig & {taskId: string}): Promise<RawChatTaskDetail> {
  const response = await fetchCAPI({
    path: taskByIdApiPath(taskId),
    copilotApiUrl: apiURL,
    additionalHeaders: {Accept: 'application/json'},
    signal,
  })
  const data = (await response.json()) as RawChatTaskDetailResponse | RawChatTaskDetail
  if ('task' in data && data.task) {
    return {...data.task, sessions: data.sessions ?? data.task.sessions}
  }
  return data as RawChatTaskDetail
}

export interface ChatTaskPage {
  tasks: ChatTask[]
  hasNextPage: boolean
}

export async function listChatTasksPage({
  apiURL,
  signal,
  pageSize = CHAT_HISTORY_PAGE_SIZE,
  page = 1,
}: ChatHistoryConfig & {page?: number} = {}): Promise<ChatTaskPage> {
  const response = await fetchCAPI({
    path: tasksApiPathWithParams({type: TASK_TYPE_CHAT, archived: false, page, pageSize}),
    copilotApiUrl: apiURL,
    additionalHeaders: {Accept: 'application/json'},
    signal,
  })
  const data = (await response.json()) as RawListChatTasksResponse
  const tasks = (data.tasks ?? []).map(task => transformTask(task))
  // Older responses omit the flag; a full page is the best available signal that more exist.
  return {tasks, hasNextPage: data.has_next_page ?? tasks.length === pageSize}
}

export async function listChatTasks(config: ChatHistoryConfig = {}): Promise<ChatTask[]> {
  const {tasks} = await listChatTasksPage(config)
  return tasks
}

export async function listGroupedChatTasks({
  apiURL,
  signal,
  maxGroups,
}: Pick<ChatHistoryConfig, 'apiURL' | 'signal'> & {maxGroups: number}): Promise<ChatTask[]> {
  const response = await fetchCAPI({
    path: groupedTasksApiPathWithParams({archived: false, maxGroups}),
    copilotApiUrl: apiURL,
    additionalHeaders: {Accept: 'application/json'},
    signal,
  })
  const data = (await response.json()) as RawListGroupedTasksResponse
  return (data.groups ?? []).flatMap(group =>
    group.tasks.map(task => transformTask({...task, nwo: group.repository?.full_name})),
  )
}

export type ConnectableChatSessionResult =
  | {
      ahpSession: AhpSessionIdentifiers
      displaySession: ChatDisplayIdentity
      sessionState?: string
      project?: ChatProject
    }
  | {reconnectError: string}

export class ChatSessionUnavailableError extends Error {
  readonly reason: 'terminal' | 'pendingSession'

  constructor(message: string, reason: 'terminal' | 'pendingSession') {
    super(message)
    this.name = 'ChatSessionUnavailableError'
    this.reason = reason
  }
}

export function classifyUnavailableChatSession(
  task: Pick<ChatTask, 'sessionCount' | 'archivedAt'> & {
    taskState?: string
    latestSessionState?: string
  },
): ChatSessionUnavailableError['reason'] {
  if (task.archivedAt) return 'terminal'
  return TERMINAL_STATES.has(task.taskState ?? '') || TERMINAL_STATES.has(task.latestSessionState ?? '')
    ? 'terminal'
    : 'pendingSession'
}

function buildAhpSessionFromTaskDetail(
  task: ChatTask,
  sessions: RawChatSession[],
): {
  ahpSession: AhpSessionIdentifiers
  displaySession: ChatDisplayIdentity
  sessionState?: string
  project?: ChatProject
} {
  if (!task.taskId) {
    throw new Error('Chat is missing a task identifier.')
  }

  if (!task.resumable) {
    throw new ChatSessionUnavailableError(
      task.resumeBlocker ?? 'This chat cannot be resumed.',
      classifyUnavailableChatSession(task),
    )
  }

  const selectedSession = pickConnectableSession(task, sessions)
  if (!selectedSession?.environment_id) {
    throw new ChatSessionUnavailableError(
      'Chat does not have a connectable session yet.',
      classifyUnavailableChatSession(task),
    )
  }

  // A chat task only carries an nwo when its sandbox was provisioned with the
  // repository attached, so this is exactly the set of project sessions; the
  // runtime needs it to keep reconnecting them under the sandbox agent.
  const project = parseProjectNwo(task.nwo)

  return {
    ahpSession: {
      taskId: task.taskId,
      environmentId: selectedSession.environment_id,
      sessionId: selectedSession.id,
    },
    displaySession: {
      taskId: task.taskId,
      environmentId: selectedSession.environment_id,
      sessionId: selectedSession.id,
      userId: selectedSession.user?.id,
      userLogin: selectedSession.user?.login,
      agentId: selectedSession.agent_id,
      agentSlug: selectedSession.agent_slug,
      createdAt: selectedSession.created_at,
      eventType: selectedSession.event_type,
      eventIdentifiers: selectedSession.event_identifiers,
      eventUrl: selectedSession.event_url,
      eventContent: selectedSession.event_content,
    },
    sessionState: selectedSession.state,
    ...(project ? {project} : {}),
  }
}

export async function chatSessionWithProjectForTaskId(
  taskId: string,
  {apiURL, signal}: ChatHistoryConfig,
): Promise<{ahpSession: AhpSessionIdentifiers; project?: ChatProject}> {
  if (!apiURL) throw new Error('Chat history is missing the Mission Control API URL.')

  const detail = await fetchChatTaskDetail({apiURL, signal, taskId})
  const sessions = detail.sessions ?? []
  const {ahpSession, project} = buildAhpSessionFromTaskDetail(transformTask(detail, sessions), sessions)
  return project ? {ahpSession, project} : {ahpSession}
}

export async function connectableChatSessionForTaskId(
  taskId: string,
  {apiURL, signal}: ChatHistoryConfig,
): Promise<ConnectableChatSessionResult> {
  if (!apiURL) throw new Error('Chat history is missing the Mission Control API URL.')

  const detail = await fetchChatTaskDetail({apiURL, signal, taskId})
  const sessions = detail.sessions ?? []

  try {
    return buildAhpSessionFromTaskDetail(transformTask(detail, sessions), sessions)
  } catch (error: unknown) {
    if (!(error instanceof ChatSessionUnavailableError)) throw error
    return {reconnectError: error.message}
  }
}

export type ChatTaskRestore =
  | {
      kind: 'interactive'
      title: string
      createdAt: string
      ownerLogin?: string
      managedSandbox: boolean
      ahpSession: AhpSessionIdentifiers
      project?: ChatProject
      environmentKind?: NonNullable<APITask['current_environment']>['kind']
    }
  | {
      kind: 'unavailable'
      taskId: string
      title: string
      createdAt: string
      ownerLogin?: string
      managedSandbox: boolean
      error: ChatSessionUnavailableError
      environmentKind?: NonNullable<APITask['current_environment']>['kind']
    }
  | {kind: 'inactive'; title: string; ownerLogin?: string}

export async function resolveChatTaskForRestore(
  taskId: string,
  {apiURL, signal, allowInteractive = true}: ChatHistoryConfig & {allowInteractive?: boolean},
): Promise<ChatTaskRestore> {
  if (!apiURL) throw new Error('Chat history is missing the Mission Control API URL.')

  const detail = await fetchChatTaskDetail({apiURL, signal, taskId})
  if (!detail.id || detail.id !== taskId) throw new Error('Chat history task response has an invalid identifier.')

  const sessions = detail.sessions ?? []
  const task = transformTask(detail, sessions)
  const ownerLogin = resolveTaskOwnerLogin(detail, parseProjectNwo(task.nwo)?.owner)
  if (!allowInteractive) return {kind: 'inactive', title: task.title, ...(ownerLogin ? {ownerLogin} : {})}

  const managedSandbox =
    detail.current_environment?.kind === 'managed-sandbox' || detail.compute?.provider === 'sandboxes'
  try {
    const {ahpSession, project} = buildAhpSessionFromTaskDetail(task, sessions)
    return {
      kind: 'interactive',
      title: task.title,
      createdAt: task.createdAt,
      ...(ownerLogin ? {ownerLogin} : {}),
      managedSandbox,
      ahpSession,
      ...(project ? {project} : {}),
      environmentKind:
        detail.current_environment?.id === ahpSession.environmentId ? detail.current_environment.kind : undefined,
    }
  } catch (error) {
    if (!(error instanceof ChatSessionUnavailableError)) throw error
    return {
      kind: 'unavailable',
      taskId,
      title: task.title,
      createdAt: task.createdAt,
      ...(ownerLogin ? {ownerLogin} : {}),
      managedSandbox,
      error,
      environmentKind: detail.current_environment?.kind,
    }
  }
}

export interface UseChatHistoryQueryParams {
  apiURL?: string
  queryKey: readonly unknown[]
  pageSize?: number
  enabled: boolean
}

/** Keeps a chat-history query fresh as Mission Control emits task events. */
function useChatHistoryInvalidation({
  apiURL,
  queryKey,
  enabled,
}: Pick<UseChatHistoryQueryParams, 'apiURL' | 'queryKey' | 'enabled'>) {
  const queryClient = useQueryClient()

  const queryKeyRef = useRef(queryKey)
  useEffect(() => {
    queryKeyRef.current = queryKey
  }, [queryKey])

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({queryKey: queryKeyRef.current})
  }, [queryClient])

  const debouncedInvalidate = useDebounce(invalidate, 3000, {maxWait: 10000})

  useAgentSessionsAlive({
    onEvent: debouncedInvalidate,
    enableCreateEvents: true,
    enableUpdateEvents: true,
    enableTaskUpdates: true,
    enabled: enabled && Boolean(apiURL),
  })

  return invalidate
}

export function useChatHistoryQuery({apiURL, queryKey, pageSize, enabled}: UseChatHistoryQueryParams) {
  const hiddenTaskIds = useHiddenEarlyTaskIds()
  const invalidate = useChatHistoryInvalidation({apiURL, queryKey, enabled})

  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const query = useQuery<ChatTask[], Error>({
    queryKey,
    queryFn: ({signal}) => listChatTasks({apiURL, signal, pageSize}),
    enabled,
    staleTime: 30_000,
  })

  return {
    query,
    tasks: useMemo(
      () => query.data?.filter(task => !hiddenTaskIds.has(task.taskId)) ?? [],
      [query.data, hiddenTaskIds],
    ),
    hasLoadError: query.isError && !query.data,
    invalidate,
  }
}

/**
 * Paginated variant of {@link useChatHistoryQuery} for surfaces that let the user walk
 * backwards through their full chat history instead of reading one fixed page.
 */
export function useChatHistoryInfiniteQuery({apiURL, queryKey, pageSize, enabled}: UseChatHistoryQueryParams) {
  const hiddenTaskIds = useHiddenEarlyTaskIds()
  const invalidate = useChatHistoryInvalidation({apiURL, queryKey, enabled})

  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const query = useInfiniteQuery<ChatTaskPage, Error, InfiniteData<ChatTaskPage>, readonly unknown[], number>({
    queryKey,
    queryFn: ({signal, pageParam}) => listChatTasksPage({apiURL, signal, pageSize, page: pageParam}),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage.hasNextPage ? allPages.length + 1 : undefined),
    enabled,
    staleTime: 30_000,
  })

  const tasks = useMemo(
    () => query.data?.pages.flatMap(page => page.tasks).filter(task => !hiddenTaskIds.has(task.taskId)) ?? [],
    [query.data, hiddenTaskIds],
  )

  return {
    query,
    tasks,
    hasLoadError: query.isError && !query.data,
    // A failed older page keeps the already-loaded pages, so it never reaches hasLoadError.
    hasFetchNextPageError: query.isFetchNextPageError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    invalidate,
  }
}

export function useGroupedChatHistoryQuery({
  apiURL,
  queryKey,
  maxGroups,
  enabled,
}: Pick<UseChatHistoryQueryParams, 'apiURL' | 'queryKey' | 'enabled'> & {maxGroups: number}) {
  const hiddenTaskIds = useHiddenEarlyTaskIds()
  const invalidate = useChatHistoryInvalidation({apiURL, queryKey, enabled})

  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const query = useQuery<ChatTask[], Error>({
    queryKey,
    queryFn: ({signal}) => listGroupedChatTasks({apiURL, signal, maxGroups}),
    enabled,
    staleTime: 30_000,
  })

  return {
    query,
    tasks: useMemo(
      () => query.data?.filter(task => !hiddenTaskIds.has(task.taskId)) ?? [],
      [query.data, hiddenTaskIds],
    ),
    hasLoadError: query.isError && !query.data,
    invalidate,
  }
}
